import { usePortalMessage, type PortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * The Order screen's **Payment** card — plan-005 OP-36 (FR-PAY-10, FR-PAY-12; design §18.6.8,
 * §18.15.1).
 *
 * Every online-payment fact the shop needs, in one card beside "Who called", "The order" and
 * "What happened": the state (each of IC-56's states distinct, a test payment marked as such), the
 * link's expiry, what was paid and when, what was refunded, the text message's delivery, and the
 * payment's own timeline. And the acts that move it on:
 *
 *   · **Send the link again / Send a new link** — the same link while it is valid, a new one after
 *     it expired, failed or was cancelled (the API decides; the worker sends it).
 *   · **Cancel link** — the only way to "Mark paid" while a link is live (FR-PAY-10: one order is
 *     never settled twice), so the screen names it for that.
 *   · **Take payment on hand-over** — cash or card at the door instead, cancelling the link.
 *   · **Refund** — Owners and Admins only (OD-P5), in full or in part, always with a reason.
 *
 * Every act answers `202`: the worker does the provider's half, and the change comes back on the
 * shop stream as `customer_order` (DD5-33), so the card re-reads rather than guessing the outcome.
 * A Viewer sees the facts and no buttons; a Member sees why "Refund" is not theirs.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Dialog, Icon, Input, Radio, Segmented, Tag, Textarea } from "@wetalk/ui";
import { money, type Money } from "@wetalk/domain";
import { useId, useState, type ReactNode } from "react";

import { GLYPH } from "../../shell/glyph.js";
import { formatMoney } from "../../money.js";
import { useGateway } from "../../api/provider.js";
import { rolesInPlainWords } from "../../shell/member-capability.js";
import type { WorkspaceRoleView } from "../../api/gateway.js";
import {
  CUSTOMER_ORDER_PAYMENT_STATE_LABEL,
  CUSTOMER_ORDER_PAYMENT_STATE_TONE,
  CUSTOMER_ORDER_REFUND_ROLE,
  HAND_OVER_PAYMENT_METHOD,
  HAND_OVER_PAYMENT_METHOD_LABEL,
  customerOrderIsRefundable,
  mayManageCustomerOrderPayment,
  mayRefundCustomerOrder,
  type HandOverPaymentMethodView,
} from "../../api/slice/customer-order-payment.js";
import type { CustomerOrderDetailView } from "../agents/[id]/orders/order-book-gateway.js";
import { ORDER_BOOK_QUERY_PREFIX } from "../agents/[id]/orders/order-book-live.js";

/** "Only Owners and Admins can refund an order." — the route's roles, in the API's words. */
export const REFUND_REFUSAL_TEXT = `Only ${rolesInPlainWords(CUSTOMER_ORDER_REFUND_ROLE)} can refund an order.`;

/**
 * Euros as a person types them — `5`, `5.5`, `5,50` — to integer minor units, or `null` when it is
 * not an amount. Never a float: the whole and fractional parts are read as integers (ADR 0014).
 */
export function minorOfTypedAmount(typed: string): number | null {
  const match = /^\s*(\d{1,6})(?:[.,](\d{1,2}))?\s*$/.exec(typed);
  if (match === null) return null;
  const whole = Number.parseInt(match[1] ?? "0", 10);
  const fraction = (match[2] ?? "").padEnd(2, "0");
  return whole * 100 + Number.parseInt(fraction === "" ? "0" : fraction, 10);
}

/** The hand-over methods this order may switch to: those its VoiceAgent accepts, both when unstated. */
export function handOverMethodsOf(accepted: readonly string[] | null): readonly HandOverPaymentMethodView[] {
  return HAND_OVER_PAYMENT_METHOD.filter((one) => accepted === null || accepted.includes(one));
}

export interface CustomerOrderPaymentPanelProps {
  readonly one: CustomerOrderDetailView;
  readonly role: WorkspaceRoleView | null;
  /** Another act on the order is running (advance, cancel…): the card's buttons wait too. */
  readonly busy: boolean;
}

export function CustomerOrderPaymentPanel({ one, role, busy }: CustomerOrderPaymentPanelProps): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [refusal, setRefusal] = usePortalMessage();
  const [done, setDone] = usePortalMessage();
  const [refunding, setRefunding] = useState(false);
  const [switching, setSwitching] = useState(false);

  const payment = one.payment;
  const state = one.paymentState;
  const reread = (): void => {
    for (const prefix of ORDER_BOOK_QUERY_PREFIX) void queryClient.invalidateQueries({ queryKey: [prefix] });
  };
  const act = {
    onSuccess: (said: PortalMessage) => {
      setRefusal(null);
      setDone(said);
      reread();
    },
    onError: (raised: unknown) => {
      setDone(null);
      setRefusal(() => (raised instanceof Error ? raised.message : portalText("finance", "m0001")));
      reread();
    },
  };

  const resend = useMutation({
    mutationFn: async (): Promise<PortalMessage> => {
      await gateway.resendCustomerOrderPaymentLink(one.reference);
      return () => state === "awaiting_online_payment"
        ? portalText("finance", "m0002")
        : portalText("finance", "m0003");
    },
    ...act,
  });
  const cancelLink = useMutation({
    mutationFn: async (): Promise<PortalMessage> => {
      await gateway.cancelCustomerOrderPaymentLink(one.reference);
      return () => portalText("finance", "m0004");
    },
    ...act,
  });
  const switchMethod = useMutation({
    mutationFn: async (method: HandOverPaymentMethodView): Promise<PortalMessage> => {
      await gateway.setCustomerOrderPaymentMethod(one.reference, method);
      return () => portalText("finance", "m0005", { value1: HAND_OVER_PAYMENT_METHOD_LABEL[method].toLowerCase() });
    },
    ...act,
    onSuccess: (said: PortalMessage) => {
      setSwitching(false);
      act.onSuccess(said);
    },
  });
  const refund = useMutation({
    mutationFn: async (request: { readonly amount: Money | null; readonly reason: string }): Promise<PortalMessage> => {
      await gateway.refundCustomerOrder(one.reference, {
        amountMinor: request.amount === null ? null : request.amount.amountMinor,
        reason: request.reason,
      });
      return () => request.amount === null
        ? portalText("finance", "m0006")
        : portalText("finance", "m0007", { value1: formatMoney(request.amount) });
    },
    ...act,
    onSuccess: (said: PortalMessage) => {
      setRefunding(false);
      act.onSuccess(said);
    },
  });

  if (payment === null && state === "not_online") return null;

  const pending = busy || resend.isPending || cancelLink.isPending || switchMethod.isPending || refund.isPending;
  const final = one.status === "delivered" || one.status === "cancelled";
  const manages = mayManageCustomerOrderPayment(role);
  const awaiting = state === "awaiting_online_payment";
  const notCompleted = state === "online_payment_not_completed";
  const linkActive = payment === null ? awaiting : payment.linkActive;
  const methods = handOverMethodsOf(one.acceptedPaymentMethod);
  const refundable = payment !== null && customerOrderIsRefundable(payment);

  return (
    <div className="c-card c-orders-payment" aria-label={portalText("finance", "m0008")}>
      <p className="c-card-kicker">{portalText("finance", "m0008")}</p>
      <p className="c-orders-payment-state">
        <Tag status={CUSTOMER_ORDER_PAYMENT_STATE_TONE[state]}>{CUSTOMER_ORDER_PAYMENT_STATE_LABEL[state]}</Tag>
        {one.paymentIsTest ? <Tag tone="outline">{portalText("finance", "m0009")}</Tag> : null}
      </p>

      {payment === null ? null : (
        <dl className="c-orders-dl">
          {payment.expiresAtText === null ? null : (
            <>
              <dt>{portalText("finance", "m0010")}</dt>
              <dd>{payment.expiresAtText}</dd>
            </>
          )}
          {payment.paidAmount === null ? null : (
            <>
              <dt>{portalText("finance", "m0011")}</dt>
              <dd>
                {formatMoney(payment.paidAmount)}
                {payment.paidAtText === null ? null : <> · {payment.paidAtText}</>}
              </dd>
            </>
          )}
          {payment.refunded.amountMinor === 0 ? null : (
            <>
              <dt>{portalText("finance", "m0012")}</dt>
              <dd>{formatMoney(payment.refunded)}</dd>
            </>
          )}
          {payment.sms === null ? null : (
            <>
              <dt>{portalText("finance", "m0013")}</dt>
              <dd>{payment.sms.text}</dd>
            </>
          )}
        </dl>
      )}

      {state === "amount_mismatch" ? (
        <p className="c-orders-sub">{portalText("finance", "m0014")}</p>
      ) : null}
      {state === "refunded" ? <p className="c-orders-sub">{portalText("finance", "m0015")}</p> : null}

      {payment === null || payment.timeline.length === 0 ? null : (
        <ol className="c-orders-timeline c-orders-payment-timeline" aria-label={portalText("finance", "m0016")}>
          {payment.timeline.map((entry, index) => (
            <li className="c-orders-timeline-row" key={`${entry.kind}-${String(index)}`}>
              <span className="c-orders-sub">{entry.atText}</span>
              <span>{entry.text}</span>
            </li>
          ))}
        </ol>
      )}

      {refusal === null ? null : (
        <p className="c-orders-refusal" role="alert">
          <Icon glyph={GLYPH.warningCircle} size="md" />
          <span>{refusal}</span>
        </p>
      )}
      {done === null ? null : (
        <p className="c-orders-notice" role="status">
          <Icon glyph={GLYPH.checkCircle} size="md" />
          <span>{done}</span>
        </p>
      )}

      <div className="c-orders-payment-actions">
        {manages && !final && (awaiting || notCompleted) ? (
          <Button
            variant="secondary"
            disabled={pending}
            onClick={() => {
              resend.mutate();
            }}
          >
            <Icon glyph={GLYPH.paperPlaneRight} size="md" />
            {awaiting ? portalText("finance", "m0017") : portalText("finance", "m0018")}
          </Button>
        ) : null}
        {manages && linkActive ? (
          <Button
            variant="secondary"
            disabled={pending}
            title={portalText("finance", "m0019")}
            onClick={() => {
              cancelLink.mutate();
            }}
          >
            <Icon glyph={GLYPH.prohibit} size="md" />{portalText("finance", "m0020")}</Button>
        ) : null}
        {manages && !final && (awaiting || notCompleted) && methods.length > 0 ? (
          <Button
            variant="secondary"
            disabled={pending}
            onClick={() => {
              setSwitching(true);
            }}
          >
            <Icon glyph={GLYPH.creditCard} size="md" />{portalText("finance", "m0021")}</Button>
        ) : null}
        {refundable && mayRefundCustomerOrder(role) ? (
          <Button
            variant="secondary"
            disabled={pending}
            onClick={() => {
              setRefunding(true);
            }}
          >
            <Icon glyph={GLYPH.arrowUDownLeft} size="md" />{portalText("finance", "m0022")}</Button>
        ) : null}
      </div>
      {refundable && !mayRefundCustomerOrder(role) && role !== null ? (
        <p className="c-orders-sub">{REFUND_REFUSAL_TEXT}</p>
      ) : null}

      {payment === null ? null : (
        <RefundDialog
          open={refunding}
          refundable={payment.refundable}
          pending={refund.isPending}
          onClose={() => {
            setRefunding(false);
          }}
          onRefund={(request) => {
            refund.mutate(request);
          }}
        />
      )}
      <SwitchMethodDialog
        open={switching}
        methods={methods}
        pending={switchMethod.isPending}
        linkActive={linkActive}
        onClose={() => {
          setSwitching(false);
        }}
        onSwitch={(method) => {
          switchMethod.mutate(method);
        }}
      />
    </div>
  );
}

function RefundDialog(props: {
  readonly open: boolean;
  readonly refundable: Money;
  readonly pending: boolean;
  readonly onClose: () => void;
  readonly onRefund: (request: { readonly amount: Money | null; readonly reason: string }) => void;
}): ReactNode {
  usePortalTranslation("finance");
  const [whole, setWhole] = useState<"full" | "part">("full");
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");

  const typedMinor = minorOfTypedAmount(typed);
  const partAmount = typedMinor === null ? null : money(typedMinor, props.refundable.currency);
  const partProblem =
    whole === "full"
      ? null
      : typed.trim() === ""
        ? portalText("finance", "m0023")
        : partAmount === null
          ? portalText("finance", "m0024")
          : partAmount.amountMinor <= 0
            ? portalText("finance", "m0025")
            : partAmount.amountMinor > props.refundable.amountMinor
              ? portalText("finance", "m0026", { value1: formatMoney(props.refundable) })
              : null;
  const amount = whole === "full" ? props.refundable : partAmount;
  const ready = reason.trim() !== "" && partProblem === null && amount !== null;
  // Review item 7 (a11y): the amount's problem is tied to its field, and a disabled Refund button
  // says why — in words on the dialog, and to a screen reader through `aria-describedby`.
  const amountProblemId = useId();
  const refundBlockedId = useId();
  const amountProblemShown = partProblem !== null && typed.trim() !== "";
  const refundBlocked = props.pending
    ? portalText("finance", "m0027")
    : partProblem !== null
      ? portalText("finance", "m0028", { value1: partProblem })
      : reason.trim() === ""
        ? portalText("finance", "m0029")
        : null;

  return (
    <Dialog
      open={props.open}
      title={portalText("finance", "m0030")}
      onClose={props.onClose}
      actions={
        <>
          <Button variant="secondary" onClick={props.onClose}>{portalText("finance", "m0031")}</Button>
          <Button
            variant="primary"
            disabled={!ready || props.pending}
            aria-describedby={refundBlocked === null ? undefined : refundBlockedId}
            onClick={() => {
              props.onRefund({ amount: whole === "full" ? null : partAmount, reason: reason.trim() });
            }}
          >
            {amount === null || partProblem !== null ? portalText("finance", "m0032") : portalText("finance", "m0033", { value1: formatMoney(amount) })}
          </Button>
        </>
      }
    >
      <p>{portalText("finance", "refund.cardExplanation", { amount: formatMoney(props.refundable) })}</p>
      <Segmented
        name="refund-amount"
        legend={portalText("finance", "m1133")}
        options={[
          { value: "full", label: portalText("finance", "m0036", { value1: formatMoney(props.refundable) }) },
          { value: "part", label: portalText("finance", "m0037") },
        ]}
        value={whole}
        onChange={setWhole}
      />
      {whole === "part" ? (
        <label className="c-orders-reason">
          <span>{portalText("finance", "m0038")}</span>
          <Input
            value={typed}
            inputMode="decimal"
            placeholder={portalText("finance", "m0039")}
            aria-invalid={amountProblemShown}
            aria-describedby={amountProblemShown ? amountProblemId : undefined}
            onChange={(event) => {
              setTyped(event.currentTarget.value);
            }}
          />
          {amountProblemShown ? (
            <span id={amountProblemId} className="c-orders-sub">
              {partProblem}
            </span>
          ) : null}
        </label>
      ) : null}
      <label className="c-orders-reason">
        <span>{portalText("finance", "m0040")}</span>
        <Textarea
          value={reason}
          maxLength={500}
          rows={3}
          placeholder={portalText("finance", "m0041")}
          onChange={(event) => {
            setReason(event.currentTarget.value);
          }}
        />
      </label>
      {refundBlocked === null ? null : (
        <p id={refundBlockedId} className="c-orders-sub">
          {refundBlocked}
        </p>
      )}
    </Dialog>
  );
}

function SwitchMethodDialog(props: {
  readonly open: boolean;
  readonly methods: readonly HandOverPaymentMethodView[];
  readonly pending: boolean;
  readonly linkActive: boolean;
  readonly onClose: () => void;
  readonly onSwitch: (method: HandOverPaymentMethodView) => void;
}): ReactNode {
  usePortalTranslation("finance");
  const [chosen, setChosen] = useState<HandOverPaymentMethodView | null>(null);
  return (
    <Dialog
      open={props.open}
      title={portalText("finance", "m0042")}
      onClose={props.onClose}
      actions={
        <>
          <Button variant="secondary" onClick={props.onClose}>{portalText("finance", "m0043")}</Button>
          <Button
            variant="primary"
            disabled={chosen === null || props.pending}
            onClick={() => {
              if (chosen !== null) props.onSwitch(chosen);
            }}
          >{portalText("finance", "m0044")}</Button>
        </>
      }
    >
      <p>{portalText("finance", "m0045")}{props.linkActive ? portalText("finance", "m0046") : ""}{portalText("finance", "m0047")}</p>
      <div className="c-orders-choice" role="radiogroup" aria-label={portalText("finance", "m0048")}>
        {props.methods.map((method) => (
          <Radio
            key={method}
            name="hand-over-method"
            label={HAND_OVER_PAYMENT_METHOD_LABEL[method]}
            checked={chosen === method}
            onChange={() => {
              setChosen(method);
            }}
          />
        ))}
      </div>
    </Dialog>
  );
}
